import React, { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import {
    Table,
    TableHeader,
    TableRow,
    TableHead,
    TableBody,
    TableCell,
} from "../components/ui/table";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "../components/ui/select";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
    DialogFooter,
} from "../components/ui/dialog";
import { Pencil, Plus, Trash2, LogOut, Package, ShoppingCart, MessageSquare, Eye, Mail, Phone, MapPin, FileText, Copy } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_OPTIONS = ["pending", "confirmed", "paid", "shipped", "cancelled"];
const STATUS_COLORS = {
    pending: "bg-amber-100 text-amber-800",
    confirmed: "bg-blue-100 text-blue-800",
    paid: "bg-emerald-100 text-emerald-800",
    shipped: "bg-purple-100 text-purple-800",
    cancelled: "bg-red-100 text-red-800",
};

export default function AdminDashboard() {
    const navigate = useNavigate();
    const token = localStorage.getItem("pepstep_admin_token");
    const auth = { headers: { Authorization: `Bearer ${token}` } };

    const [orders, setOrders] = useState([]);
    const [products, setProducts] = useState([]);
    const [messages, setMessages] = useState([]);

    useEffect(() => {
        if (!token) {
            navigate("/admin-login");
            return;
        }
        refresh();
    }, [token, navigate]);

    const refresh = async () => {
        try {
            const [o, p, m] = await Promise.all([
                axios.get(`${API}/orders`, auth),
                axios.get(`${API}/products`),
                axios.get(`${API}/contact`, auth),
            ]);
            setOrders(o.data);
            setProducts(p.data);
            setMessages(m.data);
        } catch (e) {
            if (e.response?.status === 401) {
                localStorage.removeItem("pepstep_admin_token");
                navigate("/admin-login");
            }
        }
    };

    const updateStatus = async (orderId, status) => {
        try {
            await axios.patch(`${API}/orders/${orderId}/status`, { status }, auth);
            toast.success("Status updated");
            refresh();
        } catch {
            toast.error("Failed to update");
        }
    };

    const logout = () => {
        localStorage.removeItem("pepstep_admin_token");
        navigate("/admin-login");
    };

    const stats = {
        total_orders: orders.length,
        pending: orders.filter((o) => o.status === "pending").length,
        revenue: orders.filter((o) => o.status !== "cancelled").reduce((s, o) => s + o.total, 0),
        products: products.length,
    };

    return (
        <div className="pt-28 pb-24 min-h-screen bg-pep-surface" data-testid="admin-dashboard">
            <div className="max-w-7xl mx-auto px-6 md:px-12">
                <div className="flex flex-wrap justify-between items-center gap-4 mb-8">
                    <div>
                        <p className="text-pep-primary text-sm uppercase tracking-widest font-medium mb-2">Admin</p>
                        <h1 className="font-heading text-4xl text-pep-navy font-medium">Dashboard</h1>
                    </div>
                    <button onClick={logout} className="inline-flex items-center gap-2 text-pep-muted hover:text-pep-navy text-sm" data-testid="admin-logout">
                        <LogOut className="w-4 h-4" /> Sign out
                    </button>
                </div>

                {/* Stats */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10">
                    <Stat label="Total orders" value={stats.total_orders} />
                    <Stat label="Pending" value={stats.pending} />
                    <Stat label="Revenue" value={`$${stats.revenue.toFixed(2)}`} />
                    <Stat label="Products" value={stats.products} />
                </div>

                <Tabs defaultValue="orders">
                    <TabsList className="bg-white border border-pep-border/50 rounded-full p-1">
                        <TabsTrigger value="orders" className="rounded-full" data-testid="admin-tab-orders">
                            <ShoppingCart className="w-4 h-4 mr-2" /> Orders
                        </TabsTrigger>
                        <TabsTrigger value="products" className="rounded-full" data-testid="admin-tab-products">
                            <Package className="w-4 h-4 mr-2" /> Products
                        </TabsTrigger>
                        <TabsTrigger value="messages" className="rounded-full" data-testid="admin-tab-messages">
                            <MessageSquare className="w-4 h-4 mr-2" /> Messages
                        </TabsTrigger>
                    </TabsList>

                    <TabsContent value="orders" className="mt-6">
                        <div className="bg-white rounded-3xl border border-pep-border/50 overflow-hidden">
                            <Table>
                                <TableHeader>
                                    <TableRow>
                                        <TableHead>Order #</TableHead>
                                        <TableHead>Customer</TableHead>
                                        <TableHead>Items</TableHead>
                                        <TableHead>Total</TableHead>
                                        <TableHead>Status</TableHead>
                                        <TableHead>Date</TableHead>
                                        <TableHead className="text-right">Details</TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {orders.length === 0 ? (
                                        <TableRow><TableCell colSpan={7} className="text-center py-8 text-pep-muted">No orders yet</TableCell></TableRow>
                                    ) : orders.map((o) => (
                                        <TableRow key={o.id} data-testid={`admin-order-${o.order_number}`}>
                                            <TableCell className="font-medium">{o.order_number}</TableCell>
                                            <TableCell>
                                                <div>{o.customer.full_name}</div>
                                                <div className="text-xs text-pep-muted">{o.customer.email}</div>
                                            </TableCell>
                                            <TableCell>{o.items.reduce((s, i) => s + i.quantity, 0)}</TableCell>
                                            <TableCell className="font-medium">${o.total.toFixed(2)}</TableCell>
                                            <TableCell>
                                                <Select value={o.status} onValueChange={(v) => updateStatus(o.id, v)}>
                                                    <SelectTrigger className={`h-8 rounded-full text-xs ${STATUS_COLORS[o.status]} border-0`} data-testid={`admin-status-${o.order_number}`}>
                                                        <SelectValue />
                                                    </SelectTrigger>
                                                    <SelectContent>
                                                        {STATUS_OPTIONS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                                                    </SelectContent>
                                                </Select>
                                            </TableCell>
                                            <TableCell className="text-xs text-pep-muted">{new Date(o.created_at).toLocaleString()}</TableCell>
                                            <TableCell className="text-right">
                                                <OrderDetailDialog order={o} />
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </div>
                    </TabsContent>

                    <TabsContent value="products" className="mt-6">
                        <div className="flex justify-end mb-4">
                            <ProductDialog onSaved={refresh} auth={auth} />
                        </div>
                        <div className="bg-white rounded-3xl border border-pep-border/50 overflow-hidden">
                            <Table>
                                <TableHeader>
                                    <TableRow>
                                        <TableHead>Name</TableHead>
                                        <TableHead>Category</TableHead>
                                        <TableHead>Price</TableHead>
                                        <TableHead>Stock</TableHead>
                                        <TableHead></TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {products.map((p) => (
                                        <TableRow key={p.id} data-testid={`admin-product-${p.slug}`}>
                                            <TableCell className="font-medium">{p.name}</TableCell>
                                            <TableCell><span className="text-xs uppercase tracking-widest text-pep-muted">{p.category}</span></TableCell>
                                            <TableCell>${p.price.toFixed(2)}</TableCell>
                                            <TableCell>{p.in_stock ? "In stock" : "Out"}</TableCell>
                                            <TableCell className="flex gap-2">
                                                <ProductDialog product={p} onSaved={refresh} auth={auth} />
                                                <button
                                                    onClick={async () => {
                                                        if (window.confirm(`Delete ${p.name}?`)) {
                                                            await axios.delete(`${API}/products/${p.id}`, auth);
                                                            toast.success("Deleted");
                                                            refresh();
                                                        }
                                                    }}
                                                    className="p-2 text-pep-muted hover:text-red-600"
                                                    data-testid={`admin-product-delete-${p.slug}`}
                                                >
                                                    <Trash2 className="w-4 h-4" />
                                                </button>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </div>
                    </TabsContent>

                    <TabsContent value="messages" className="mt-6">
                        <div className="space-y-3">
                            {messages.length === 0 ? (
                                <p className="text-center text-pep-muted py-8">No messages yet</p>
                            ) : messages.map((m) => (
                                <div key={m.id} className="bg-white rounded-2xl p-5 border border-pep-border/50" data-testid={`admin-message-${m.id}`}>
                                    <div className="flex justify-between flex-wrap gap-2 mb-2">
                                        <p className="font-medium text-pep-navy">{m.name} <span className="text-pep-muted text-sm">&lt;{m.email}&gt;</span></p>
                                        <p className="text-xs text-pep-muted">{new Date(m.created_at).toLocaleString()}</p>
                                    </div>
                                    {m.subject && <p className="text-sm text-pep-primary font-medium mb-2">{m.subject}</p>}
                                    <p className="text-pep-navy whitespace-pre-wrap text-sm">{m.message}</p>
                                </div>
                            ))}
                        </div>
                    </TabsContent>
                </Tabs>
            </div>
        </div>
    );
}

function Stat({ label, value }) {
    return (
        <div className="bg-white rounded-3xl p-6 border border-pep-border/50">
            <p className="text-xs uppercase tracking-widest text-pep-muted">{label}</p>
            <p className="font-heading text-3xl text-pep-navy font-medium mt-2">{value}</p>
        </div>
    );
}

function OrderDetailDialog({ order }) {
    const [open, setOpen] = useState(false);
    const c = order.customer;

    const copy = (text, label) => {
        navigator.clipboard.writeText(text);
        toast.success(`${label} copied`);
    };

    const fullAddress = [
        c.address_line1,
        c.address_line2,
        `${c.city}${c.state ? ", " + c.state : ""} ${c.postal_code}`,
        c.country,
    ].filter(Boolean).join("\n");

    return (
        <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
                <button
                    className="inline-flex items-center gap-1.5 text-pep-primary hover:text-pep-navy text-sm font-medium"
                    data-testid={`admin-order-view-${order.order_number}`}
                >
                    <Eye className="w-4 h-4" /> View
                </button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                    <DialogTitle className="font-heading text-2xl flex items-center justify-between gap-3">
                        <span>Order {order.order_number}</span>
                        <span className={`text-xs font-medium px-3 py-1 rounded-full ${STATUS_COLORS[order.status]}`}>
                            {order.status}
                        </span>
                    </DialogTitle>
                </DialogHeader>

                <div className="space-y-6 mt-2">
                    {/* Customer */}
                    <section>
                        <h3 className="text-xs uppercase tracking-widest text-pep-muted mb-3">Customer</h3>
                        <div className="bg-pep-surface rounded-2xl p-4 space-y-3">
                            <DetailRow label="Name" value={c.full_name} onCopy={() => copy(c.full_name, "Name")} />
                            <DetailRow
                                label="Email"
                                icon={Mail}
                                value={<a href={`mailto:${c.email}`} className="text-pep-primary hover:underline">{c.email}</a>}
                                onCopy={() => copy(c.email, "Email")}
                            />
                            {c.phone && (
                                <DetailRow
                                    label="Phone"
                                    icon={Phone}
                                    value={<a href={`tel:${c.phone}`} className="text-pep-primary hover:underline">{c.phone}</a>}
                                    onCopy={() => copy(c.phone, "Phone")}
                                />
                            )}
                        </div>
                    </section>

                    {/* Shipping address */}
                    <section>
                        <h3 className="text-xs uppercase tracking-widest text-pep-muted mb-3">Shipping address</h3>
                        <div className="bg-pep-surface rounded-2xl p-4">
                            <div className="flex items-start justify-between gap-3">
                                <div className="flex gap-3 flex-1">
                                    <MapPin className="w-4 h-4 text-pep-primary mt-0.5 flex-shrink-0" />
                                    <div className="text-sm text-pep-navy whitespace-pre-line leading-relaxed">{fullAddress}</div>
                                </div>
                                <button
                                    onClick={() => copy(`${c.full_name}\n${fullAddress}`, "Address")}
                                    className="text-pep-muted hover:text-pep-primary p-1"
                                    title="Copy full address"
                                >
                                    <Copy className="w-4 h-4" />
                                </button>
                            </div>
                        </div>
                    </section>

                    {/* Notes */}
                    {c.notes && (
                        <section>
                            <h3 className="text-xs uppercase tracking-widest text-pep-muted mb-3">Order notes</h3>
                            <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4 flex gap-3">
                                <FileText className="w-4 h-4 text-amber-700 mt-0.5 flex-shrink-0" />
                                <p className="text-sm text-amber-900 whitespace-pre-wrap leading-relaxed">{c.notes}</p>
                            </div>
                        </section>
                    )}

                    {/* Items */}
                    <section>
                        <h3 className="text-xs uppercase tracking-widest text-pep-muted mb-3">Items</h3>
                        <div className="bg-white border border-pep-border/60 rounded-2xl divide-y divide-pep-border/50">
                            {order.items.map((i, idx) => (
                                <div key={idx} className="flex justify-between items-center px-4 py-3">
                                    <div>
                                        <p className="text-sm font-medium text-pep-navy">{i.name}</p>
                                        <p className="text-xs text-pep-muted">${i.price.toFixed(2)} × {i.quantity}</p>
                                    </div>
                                    <p className="text-sm font-medium text-pep-navy">${(i.price * i.quantity).toFixed(2)}</p>
                                </div>
                            ))}
                        </div>
                    </section>

                    {/* Totals */}
                    <section>
                        <h3 className="text-xs uppercase tracking-widest text-pep-muted mb-3">Totals</h3>
                        <div className="bg-pep-surface rounded-2xl p-4 space-y-1.5 text-sm">
                            <div className="flex justify-between"><span className="text-pep-muted">Subtotal</span><span>${order.subtotal.toFixed(2)}</span></div>
                            {order.discount_amount > 0 && (
                                <div className="flex justify-between text-pep-primary">
                                    <span>Bundle discount ({order.bundle_discount_percent}%)</span>
                                    <span>−${order.discount_amount.toFixed(2)}</span>
                                </div>
                            )}
                            {typeof order.shipping_cost === "number" && (
                                <div className="flex justify-between"><span className="text-pep-muted">Shipping</span><span>{order.shipping_cost === 0 ? "FREE" : `$${order.shipping_cost.toFixed(2)}`}</span></div>
                            )}
                            <div className="flex justify-between font-heading text-lg pt-2 border-t border-pep-border/60 mt-2">
                                <span className="text-pep-navy">Total</span>
                                <span className="text-pep-primary">${order.total.toFixed(2)}</span>
                            </div>
                            <div className="flex justify-between text-xs text-pep-muted pt-2">
                                <span>Payment method</span>
                                <span className="capitalize">{order.payment_method.replace("_", " ")}</span>
                            </div>
                            <div className="flex justify-between text-xs text-pep-muted">
                                <span>Placed</span>
                                <span>{new Date(order.created_at).toLocaleString()}</span>
                            </div>
                        </div>
                    </section>
                </div>
            </DialogContent>
        </Dialog>
    );
}

function DetailRow({ label, value, icon: Icon, onCopy }) {
    return (
        <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-2 min-w-0 flex-1">
                {Icon && <Icon className="w-4 h-4 text-pep-primary flex-shrink-0" />}
                <div className="min-w-0 flex-1">
                    <p className="text-[10px] uppercase tracking-widest text-pep-muted">{label}</p>
                    <div className="text-sm text-pep-navy truncate">{value}</div>
                </div>
            </div>
            {onCopy && (
                <button onClick={onCopy} className="text-pep-muted hover:text-pep-primary p-1 flex-shrink-0" title={`Copy ${label}`}>
                    <Copy className="w-3.5 h-3.5" />
                </button>
            )}
        </div>
    );
}


function ProductDialog({ product, onSaved, auth }) {
    const [open, setOpen] = useState(false);
    const isEdit = !!product;
    const [form, setForm] = useState(
        product || { name: "", slug: "", price: 0, description: "", category: "peptide", image: "", in_stock: true, purity: "≥99%", size: "" }
    );
    const [saving, setSaving] = useState(false);

    const save = async () => {
        setSaving(true);
        try {
            if (isEdit) {
                await axios.patch(`${API}/products/${product.id}`, form, auth);
                toast.success("Product updated");
            } else {
                await axios.post(`${API}/products`, form, auth);
                toast.success("Product created");
            }
            setOpen(false);
            onSaved();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Save failed");
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
                <button
                    className={isEdit ? "p-2 text-pep-muted hover:text-pep-primary" : "inline-flex items-center gap-2 bg-pep-primary text-white rounded-full px-4 py-2 text-sm font-medium hover:bg-pep-navy"}
                    data-testid={isEdit ? `admin-edit-${product.slug}` : "admin-add-product"}
                >
                    {isEdit ? <Pencil className="w-4 h-4" /> : <><Plus className="w-4 h-4" /> Add product</>}
                </button>
            </DialogTrigger>
            <DialogContent className="max-w-lg">
                <DialogHeader>
                    <DialogTitle className="font-heading">{isEdit ? "Edit product" : "New product"}</DialogTitle>
                </DialogHeader>
                <div className="space-y-3">
                    <Input placeholder="Name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="admin-product-name" />
                    {!isEdit && (
                        <Input placeholder="Slug (e.g. retatrutide-15mg)" value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value })} data-testid="admin-product-slug" />
                    )}
                    <Input type="number" placeholder="Price" value={form.price} onChange={(e) => setForm({ ...form, price: parseFloat(e.target.value) || 0 })} data-testid="admin-product-price" />
                    <Input placeholder="Category" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} />
                    <Input placeholder="Size (e.g. 10mg)" value={form.size} onChange={(e) => setForm({ ...form, size: e.target.value })} />
                    <Input placeholder="Image URL" value={form.image} onChange={(e) => setForm({ ...form, image: e.target.value })} />
                    <Textarea rows={3} placeholder="Description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
                </div>
                <DialogFooter>
                    <button onClick={save} disabled={saving} className="bg-pep-primary text-white rounded-full px-6 py-2.5 font-medium hover:bg-pep-navy disabled:opacity-50" data-testid="admin-product-save">
                        {saving ? "Saving..." : "Save"}
                    </button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
